import React, { useState, useEffect, useContext } from "react";
import { useIntl } from "react-intl";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import { useHistory } from "react-router-dom";
import moment from "moment-timezone";
import { FlitServiceContext } from "../../../services/flitService";

const GOOGLE_API_KEY = process.env.REACT_APP_GOOGLE_API_KEY;
const CALENDAR_ID = "en.usa%23holiday@group.v.calendar.google.com";

// Fixed palette cycled per consol schedule so all events of one schedule share a color.
const CONSOL_SCHEDULE_COLORS = [
  { backgroundColor: "#D7F5DF", borderColor: "#54B37A", textColor: "#255C3C" },
  { backgroundColor: "#FCE1D5", borderColor: "#E97B5F", textColor: "#8A3A24" },
  { backgroundColor: "#DCE9FC", borderColor: "#5C8DE0", textColor: "#25457E" },
  { backgroundColor: "#FBF0D0", borderColor: "#D8A927", textColor: "#77590E" },
];

// masterBillNo is used as "[schedule name] ship name" (e.g. "[8월 1차] SM YANTIAN"),
// so break the leading bracketed part onto its own line.
function formatMasterBillNo(masterBillNo) {
  if (!masterBillNo) return "";
  const match = masterBillNo.match(/^([[(][^\])]*[\])])\s*(.+)$/);
  if (match) {
    return match[1] + "\n" + match[2];
  }
  return masterBillNo;
}

function ShipmentCalendar({ activeShipments, deliveredShipments }) {
  const history = useHistory();
  const intl = useIntl();
  const { flitSvc } = useContext(FlitServiceContext);
  const [shipmentEvents, setShipmentEvents] = useState([]);
  const [holidayEvents, setHolidayEvents] = useState([]);
  const [consolScheduleEvents, setConsolScheduleEvents] = useState([]);

  useEffect(() => {
    const events = [];

    const shipments = [
      ...(activeShipments || []),
      ...(deliveredShipments || []),
    ];

    shipments.forEach(item => {
      events.push({
        id: item.id,
        title: intl.formatMessage({ id: "PICKUP" }),
        start: moment(
          item.originPickupOn || item.estimatedOriginPickupOn
        ).format("YYYY-MM-DD"),
        backgroundColor: "#FFA800",
        borderColor: "#FFA800",
        allDay: true,
        url: `/shipment/${item.id}`,
      });
      events.push({
        id: item.id,
        title: intl.formatMessage({ id: "DELIVERY" }),
        start: moment(item.deliveredOn || item.estimatedDeliveredOn).format(
          "YYYY-MM-DD"
        ),
        allDay: true,
        url: `/shipment/${item.id}`,
      });
    });

    setShipmentEvents(events);
  }, [activeShipments, deliveredShipments, intl]);

  useEffect(() => {
    flitSvc
      .getAvailableConsolSchedule()
      .then(({ data }) => {
        const events = [];

        (data || [])
          .filter(schedule => schedule.masterBillNo)
          .forEach((schedule, index) => {
            const colors =
              CONSOL_SCHEDULE_COLORS[index % CONSOL_SCHEDULE_COLORS.length];
            const name = formatMasterBillNo(schedule.masterBillNo);

            const pushEvent = (date, labelId) => {
              if (!date) return;
              events.push({
                id: `consol-schedule-${schedule.id}-${labelId}`,
                title: name + "\n" + intl.formatMessage({ id: labelId }),
                start: moment(date).format("YYYY-MM-DD"),
                allDay: true,
                ...colors,
                extendedProps: { isConsolSchedule: true },
              });
            };

            pushEvent(
              schedule.estimatedDepartureOn,
              "CONSOL_CALENDAR_DEPARTURE"
            );
            pushEvent(schedule.estimatedArrivalOn, "CONSOL_CALENDAR_ARRIVAL");

            const documentCutOff = schedule.documentCutOffOn
              ? moment(schedule.documentCutOffOn).format("YYYY-MM-DD")
              : null;
            const freightCutOff = schedule.freightCutOffOn
              ? moment(schedule.freightCutOffOn).format("YYYY-MM-DD")
              : null;

            if (documentCutOff && documentCutOff === freightCutOff) {
              pushEvent(
                schedule.documentCutOffOn,
                "CONSOL_CALENDAR_DOC_FREIGHT_CUT_OFF"
              );
            } else {
              pushEvent(
                schedule.documentCutOffOn,
                "CONSOL_CALENDAR_DOCUMENT_CUT_OFF"
              );
              pushEvent(
                schedule.freightCutOffOn,
                "CONSOL_CALENDAR_FREIGHT_CUT_OFF"
              );
            }
          });

        setConsolScheduleEvents(events);
      })
      .catch(error => {
        console.error("Error fetching consol schedules:", error);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [intl]);

  useEffect(() => {
    if (!GOOGLE_API_KEY) {
      console.error("Google API key is missing");
      return;
    }

    const getHolidays = async () => {
      try {
        const response = await fetch(
          `https://www.googleapis.com/calendar/v3/calendars/${CALENDAR_ID}/events?key=${GOOGLE_API_KEY}`
        );
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        const holidays = data.items.map(item => ({
          title: item.summary,
          start: item.start.date,
          allDay: true,
          backgroundColor: "#E32B00",
          borderColor: "#E32B00",
        }));
        setHolidayEvents(holidays);
      } catch (error) {
        console.error("Error fetching holidays:", error);
      }
    };

    getHolidays();
  }, []);

  const allEvents = [...shipmentEvents, ...consolScheduleEvents, ...holidayEvents];

  return (
    <FullCalendar
      plugins={[dayGridPlugin]}
      headerToolbar={{
        left: "",
        center: "title",
        right: "prev,next today",
      }}
      initialView="dayGridMonth"
      events={allEvents}
      locale={intl.locale}
      eventContent={({ event }) => {
        if (!event.extendedProps.isConsolSchedule) {
          return <div className="fc-event-title">{event.title}</div>;
        }
        return (
          <div
            style={{
              whiteSpace: "pre-line",
              textAlign: "center",
              width: "100%",
              overflow: "hidden",
              lineHeight: 1.3,
              padding: "1px 2px",
            }}
          >
            {event.title}
          </div>
        );
      }}
      eventClick={({ event }) => {
        if (event.url) {
          history.push(event.url);
        }
      }}
    />
  );
}

export default ShipmentCalendar;
